import React, { useCallback } from 'react';

import { useStoreContext } from 'features/store';
import { useIntl } from 'react-intl';

import { IMainMenuViewOption } from '@rbi-ctg/menu';
import { useMaybePlayAudioOnceOnMount } from 'components/app-takeover/play-audio-once';
import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import { Helmet } from 'components/helmet';
import { usePullToReload } from 'components/pull-to-reload/pull-to-reload';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useMenuScroll } from 'hooks/use-menu-scroll';
import { useFocusEffect } from 'navigation/hooks/use-focus-effect';
import MenuTileGrid from 'pages/menu/components/menu-tile-grid';
import { MenuSkeletonLoader } from 'pages/menu/components/menu-tile-grid/menu-tile-grid-loading';
import { useMenuContext } from 'state/menu';
import { useServiceModeContext } from 'state/service-mode';
import { EventName, emitEvent } from 'utils/event-hub';
import { waitForDuration } from 'utils/wait-for-duration';

import { MenuReorderWidget } from '../components/menu-reorder-widget';
import withMenuEmptyState from '../components/with-menu-empty-state';
import { useFetchMainMenu, useFetchMainMenuOnMount } from '../hooks/use-fetch-main-menu';

import { MenuEmptyState } from './menu-empty-state';
import { excludeDisabledMenuSections } from './utils';

const MainMenuComponent = () => {
  useRumPageView('main-menu', 'Main Menu');
  const fetchMainMenu = useFetchMainMenu();
  const { store } = useStoreContext();

  const { formatMessage } = useIntl();

  const { serviceMode } = useServiceModeContext();
  const {
    activeDayParts,
    allPricingDependenciesAreSettled,
    forceRefreshActiveDayParts,
    showBurgersForBreakfast,
    hasError,
    data,
    getPricingAndAvailability,
    isMenuLoading,
  } = useMenuContext();

  const menuData = excludeDisabledMenuSections({
    menuData: data?.Menu?.options as IMainMenuViewOption[] | undefined,
    activeDayParts,
    showBurgersForBreakfast,
    serviceMode,
    getPricingAndAvailability,
  });

  const onRefresh = async (): Promise<void> => {
    // This is just an animation feedback of one second
    await waitForDuration(1000);
    forceRefreshActiveDayParts();
    fetchMainMenu(true);
  };

  useMenuScroll();

  useFocusEffect(
    useCallback(() => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'menu',
      });
    }, [])
  );

  const { isPosDataLoading } = useStoreContext();
  const isFetchMainMenuPending = useFetchMainMenuOnMount();
  const loading =
    isFetchMainMenuPending || isMenuLoading || isPosDataLoading || (!data && !hasError);

  const hasMenuData = !!menuData?.length && allPricingDependenciesAreSettled;

  const pullToReloadProps = usePullToReload({
    onRefresh,
    isDisabled: loading,
  });

  useMaybePlayAudioOnceOnMount(useAppTakeover()?.menu?.audio, {
    shouldPlay: hasMenuData && !hasError,
    globalPlayOnce: true,
  });

  const menuContent =
    hasMenuData && !hasError ? (
      <MenuTileGrid data={menuData} {...pullToReloadProps} />
    ) : (
      <MenuEmptyState
        logWhenTrue={!loading}
        logContext={{
          activeDayParts: activeDayParts.map(dayPart => dayPart.key),
          showBurgersForBreakfast,
          serviceMode,
          isFetchedMenuEmpty: !hasMenuData,
          isPricingAndDependenciesSettled: allPricingDependenciesAreSettled,
          storeId: store.number,
          storePosVendor: store.pos?.vendor,
        }}
      />
    );

  return (
    <>
      <Helmet title={formatMessage({ id: 'mainMenu' })} />
      {loading ? (
        <MenuSkeletonLoader />
      ) : (
        <>
          <MenuReorderWidget />
          {menuContent}
        </>
      )}
    </>
  );
};

// MainMenu.whyDidYouRender = true;
export const MainMenuPage = withMenuEmptyState(MainMenuComponent);
